import { z } from 'zod'

import { lenientList, moneySchema, percentSchema } from '@/schemas/common'
import { tradeRowSchema } from '@/schemas/floorsheet'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * One company for the Stock page: its identity, its last-session quote and
 * its profile, from ONE read — `GET /api/v5/company/{symbol}/overview/`
 * (owner decision 3, 2026-10-03). It replaces the six bounded market lists
 * this used to be stitched from (`/market/search/`, `/live/`, `/movers/`,
 * `/trading-average/` and both `/fifty-two-week/` bases).
 *
 *   identity  ← `profile`  (name, sector display name)
 *   quote     ← `price`    (null when mm-be holds no current snapshot)
 *   activity  ← `price`    (volume, turnover, trades, 52-week range)
 *   profile   ← `profile`  (about, founded, contacts, share counts, holdings)
 *   mkt cap   ← COMPUTED here: `profile.listed_shares × price.ltp`
 *
 * The top-level field names are the ones the header, Overview and Technical
 * already read, kept so the switch moved no consumer: `quote.change` IS the
 * overview's `point_change`, `transactions` IS its `total_trade`.
 * `averageVolume20d` is gone — the overview publishes no volume average, and
 * the one place it was printed (Overview's Price info) now shows the frame's
 * own "30-day average", `quote.thirtyDayAverage`, instead.
 *
 * Null means "the API did not report it", and a screen leaves the stat off or
 * dashes it rather than printing a placeholder.
 */
export const companyQuoteSchema = z.object({
  ltp: moneySchema,
  /**
   * The session's move in percent. Nullable on the wire, though mm-be's
   * column is NOT NULL today — a screen must still draw no move rather than a
   * flat one when it is missing.
   */
  changePercent: percentSchema.nullable(),
  /** Rupee move (`point_change`). Null when mm-be has no previous close. */
  change: moneySchema.nullable(),
  open: moneySchema,
  high: moneySchema,
  low: moneySchema,
  close: moneySchema.nullable(),
  previousClose: moneySchema.nullable(),
  /**
   * The mean CLOSE over the last 30 trading sessions — a price, the frame's
   * Price info "30-day average". Null when fewer than 30 are on record.
   */
  thirtyDayAverage: moneySchema.nullable(),
  /** The session this quote is from — Price info's "as of" date. */
  asOf: isoDateSchema,
})

/**
 * The company's standing facts. Every field but the two codes can be null:
 * mm-be's legacy tables hold these unevenly, and a blank string is mapped to
 * null so "—" is the one empty state a screen draws.
 */
export const companyProfileSchema = z.object({
  about: z.string().min(1).nullable(),
  /** AD year of `operation_date` — the frame's "Founded 1984". */
  foundedYear: z.number().int().nullable(),
  website: z.string().min(1).nullable(),
  phone: z.string().min(1).nullable(),
  address: z.string().min(1).nullable(),
  /**
   * mm-be's absolute logo URL. NOT for rendering: the web builds every logo
   * URL itself (`VITE_COMPANY_LOGO_BASE_URL`) and `StockAvatar` owns the
   * fallbacks, because mm-be's base has 404'd before. Kept for that fallback.
   */
  logoUrl: z.string().min(1).nullable(),
  /**
   * No sign check here or on the counts below: mm-be's `IntegerField` promises
   * none, and refusing one broken legacy row would fail the page's main read.
   * `mapOverview` reads a negative count as not reported instead.
   */
  listedShares: z.number().int().nullable(),
  floatedShares: z.number().int().nullable(),
  /** Rupees, from the company's latest fundamentals row. */
  paidUpCapital: moneySchema.nullable(),
  /** Percent of listed shares. The Share holding tab reads `/shareholding/`'s own split. */
  promoterHolding: percentSchema.nullable(),
  publicHolding: percentSchema.nullable(),
  /** The sector's SYMBOL — what `/market/sectors/{symbol}/stats/` takes. */
  sectorSymbol: z.string().min(1).nullable(),
  /** mm-be's free-text codes ("STOCK", …), passed through for display. */
  companyType: z.string().min(1).nullable(),
  status: z.string().min(1).nullable(),
  /** Per share, face value plus premium. Null for a company mm-be has no IPO record of. */
  ipoPrice: moneySchema.nullable(),
})

export const companyDetailSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  /** Display name of the sector, as mm-be names it: "Commercial Banks". */
  sector: z.string().min(1).nullable(),
  /** Null when the company is listed but mm-be holds no current price snapshot. */
  quote: companyQuoteSchema.nullable(),
  /**
   * COMPUTED, not served: listed shares × last traded price, to 2 dp. Null
   * when either is missing, or when the profile lists 0 shares. mm-be's own
   * market cap (from its daily fundamentals) reaches the web only through
   * `/company/compare/`.
   */
  marketCap: moneySchema.nullable(),
  volume: z.number().int().nullable(),
  turnover: moneySchema.nullable(),
  /** The session's trade count (`total_trade`). */
  transactions: z.number().int().nullable(),
  week52High: moneySchema.nullable(),
  week52Low: moneySchema.nullable(),
  profile: companyProfileSchema,
})

export type CompanyQuote = z.infer<typeof companyQuoteSchema>
export type CompanyProfile = z.infer<typeof companyProfileSchema>
export type CompanyDetail = z.infer<typeof companyDetailSchema>

/**
 * One print of this company's floorsheet: the Floorsheet screen's own trade row
 * plus the houses by name, which the "Largest trades" table draws under their
 * codes. Null when mm-be's broker master has no entry for the code.
 */
export const companyTradeSchema = tradeRowSchema.extend({
  buyerName: z.string().min(1).nullable(),
  sellerName: z.string().min(1).nullable(),
  /**
   * The raw timestamp, which carries the DATE `time` leaves out: the floorsheet's
   * latest session can lag the price date by a day, so a section of these names
   * the day they are from. Null for a print NEPSE published without a stamp.
   */
  tradedAt: z.string().min(1).nullable(),
})

/** A list of prints that drops the one it cannot read rather than the whole table. */
export const companyTradesSchema = lenientList(companyTradeSchema, 'floorsheet trade')

/** One server page of a company's floorsheet — `total` is `meta.total`. */
export const companyFloorsheetPageSchema = z.object({
  rows: companyTradesSchema,
  total: z.number().int().nonnegative(),
})

export type CompanyTrade = z.infer<typeof companyTradeSchema>
export type CompanyFloorsheetPage = z.infer<typeof companyFloorsheetPageSchema>
